import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addHullPolygonsToMap, getObstaclesToRender } from '../../utils/hullMapLayer';
import { T } from '../../assets/theme';
import { useSurfaceStyles } from '../../assets/SurfaceStyleContext';
import { DraggableModal, ResizeHandle } from '../../components/UI';
import { useResizableHeight } from '../../utils/useResizableHeight';
import { getObstacleIcon } from '../../utils/markers';
import { assessProjectArrivals, saveStarScreenshot, deleteStarScreenshot } from '../../api/client';
import MapMeasureTool from '../../components/MapMeasureTool';
import MapCoordinateViewer from '../../components/MapCoordinateViewer';
import { useShield } from '../../utils/useShield';
import ShieldControl from '../../components/ShieldControl';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TILE_LAYERS = {
    dark: { label: '🌙 Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '© CARTO' },
    street: { label: '🗺 Street', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '© OSM' },
    satellite: { label: '🛰 Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '© Esri' },
};

// Role-based styling for STAR arrival procedure features (ARRIVAL_CLEARANCE_ENGINE.md)
// Defaults only — the derived `starRoleMeta` built inside the component (via
// getStyle('star_ois.*')) is what's actually used, so Settings customization applies.
const DEFAULT_STAR_ROLE_META = {
    track:     { weight: 3,   label: 'Track' },
    primary:   { weight: 2.5, label: 'Primary Area' },
    secondary: { weight: 2,   label: 'Secondary Area' },
};
function starResolveRole(feature) {
    return feature.type === 'point' ? 'waypoint' : feature.role;
}
function starAddArrowhead(map, latlngs, color, opacity) {
    const n = latlngs.length;
    if (n < 2) return null;
    const p1 = latlngs[n - 2], p2 = latlngs[n - 1];
    const ang = Math.atan2(p2[1] - p1[1], p2[0] - p1[0]) * 180 / Math.PI;
    const marker = L.marker(p2, {
        icon: L.divIcon({
            className: '',
            html: `<div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-bottom:12px solid ${color};transform:rotate(${90 + ang}deg);transform-origin:center;opacity:${opacity};"></div>`,
            iconSize: [12, 12], iconAnchor: [6, 6],
        }),
        interactive: false,
    });
    marker.addTo(map);
    return marker;
}
// Waypoint symbology per ARRIVAL_CLEARANCE_ENGINE.md — fly-by/fly-over ICAO symbols, never plain circles.
function waypointIcon(turnType) {
    const url = turnType === 'flyover' ? '/symbols/flyover.svg' : '/symbols/flyby.svg';
    return L.icon({ iconUrl: url, iconSize: [22, 22], iconAnchor: [11, 11] });
}
// Permanent text label flowing to the right of a waypoint symbol — a plain
// divIcon (not a Leaflet tooltip) so it never hides on mouseout and never
// intercepts clicks meant for the symbol underneath.
function waypointLabel(map, latlng, text) {
    const marker = L.marker(latlng, {
        icon: L.divIcon({
            className: '',
            html: `<div style="white-space:nowrap; padding-left:8px; font-size:9px; font-weight:700; color:#fff; text-shadow:1px 1px 2px #000,-1px -1px 2px #000,1px -1px 2px #000,-1px 1px 2px #000;">${text}</div>`,
            iconSize: [140, 14],
            iconAnchor: [0, 7],
        }),
        interactive: false,
    });
    marker.addTo(map);
    return marker;
}

const exportToCsv = (headers, rows, filename) => {
    const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(val => {
            const cleanVal = String(val ?? '').replace(/—/g, '-');
            return `"${cleanVal.replace(/"/g, '""')}"`;
        }).join(","))
    ].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const EMPTY_RESULT = { obstacle_count: 0, procedures: [] };

export default function ArrivalOisPage({ project, obstacles, loading: projectLoading, state, setState, showObstacles = true, showObsLabels, onObstacleContextMenu, onMapReady }) {
    const { getStyle } = useSurfaceStyles();
    const starRoleMeta = useMemo(() => {
        const out = {};
        Object.entries(DEFAULT_STAR_ROLE_META).forEach(([role, base]) => {
            const s = getStyle(`star_ois.${role}`);
            out[role] = { ...base, color: s.color, opacity: 1, fillColor: s.color, fillOpacity: s.opacity };
        });
        return out;
    }, [getStyle]);
    const mapRef = useRef(null);
    const mapInst = useRef(null);
    const tileRef = useRef(null);
    const obsLyrs = useRef([]);
    const starLyrs = useRef([]);

    const [mapMode, setMapMode] = useState('street');
    const [mapState, setMapState] = useState(null);
    const [exportingAixm, setExportingAixm] = useState(false);
    const [result, setResult] = useState(state?.starResult || EMPTY_RESULT);
    // Step 2 reference set — this project's own obstacles that actually
    // penetrate a STAR OIS surface across ANY procedure, not just its
    // tallest point overall (and not just the currently active procedure).
    const penetratingObstacleIds = useMemo(() => (
        (result.procedures || []).flatMap(p => (p.results || []).filter(r => r.verdict === 'IMPACT' || r.clears === false).map(r => r.obstacle_id))
    ), [result]);
    const { shieldOn, toggleShield, shieldData, shieldLoading, shieldError, projectMaxTotalElev, projectPenetratingMaxTotalElev, radiusKm, setRadiusKm, exportCsv } = useShield(mapInst, obstacles, penetratingObstacleIds);
    const [visibleIds, setVisibleIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeId, setActiveId] = useState(null);
    const [resultsCollapsed, setResultsCollapsed] = useState(false);
    const [resultsHeight, startResultsResize] = useResizableHeight(340, { min: 120, max: 700 });
    const [resultsPoppedOut, setResultsPoppedOut] = useState(false);
    const [capturedPics, setCapturedPics] = useState({});

    const loadAssessment = useCallback(async () => {
        if (!project?.id) return;
        setLoading(true);
        setError('');
        try {
            const { data } = await assessProjectArrivals(project.id);
            setResult(data || EMPTY_RESULT);
            const procs = data?.procedures || [];
            setVisibleIds(new Set(procs.map(p => p.id)));
            if (procs.length > 0) setActiveId(procs[0].id);
        } catch (err) {
            console.error('Failed to load Arrival assessment', err);
            setError(err.response?.data?.detail || err.message);
        } finally {
            setLoading(false);
        }
    }, [project?.id]);

    useEffect(() => { loadAssessment(); }, [loadAssessment]);

    useEffect(() => {
        if (result && setState) {
            setState(prev => {
                if (JSON.stringify(prev?.starResult) === JSON.stringify(result)) return prev;
                return { ...prev, starResult: result };
            });
        }
    }, [result, setState]);

    useEffect(() => {
        if (!mapRef.current || mapInst.current) return;
        mapInst.current = L.map(mapRef.current, { center: [-28.5, 25.0], zoom: 6, zoomDelta: 0.7, zoomSnap: 0.7 });
        tileRef.current = L.tileLayer(TILE_LAYERS[mapMode].url, { attribution: TILE_LAYERS[mapMode].attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);

        const ro = new ResizeObserver(() => mapInst.current?.invalidateSize());
        ro.observe(mapRef.current);
        setMapState(mapInst.current);
        onMapReady?.(mapInst.current);

        return () => {
            ro.disconnect();
            mapInst.current?.remove();
            mapInst.current = null;
            tileRef.current = null;
            setMapState(null);
            onMapReady?.(null);
        };
    }, []);

    useEffect(() => {
        if (!mapInst.current) return;
        if (tileRef.current) mapInst.current.removeLayer(tileRef.current);
        const layer = TILE_LAYERS[mapMode];
        tileRef.current = L.tileLayer(layer.url, { attribution: layer.attribution, maxZoom: 24, maxNativeZoom: 19 }).addTo(mapInst.current);
    }, [mapMode]);

    const procedures = result.procedures || [];
    const activeProc = procedures.find(p => p.id === activeId) || null;

    const allVisible = procedures.length > 0 && procedures.every(p => visibleIds.has(p.id));
    const someVisible = procedures.some(p => visibleIds.has(p.id)) && !allVisible;
    const toggleAll = (checked) => setVisibleIds(checked ? new Set(procedures.map(p => p.id)) : new Set());
    const toggleOne = (id, checked) => setVisibleIds(prev => {
        const next = new Set(prev);
        if (checked) next.add(id); else next.delete(id);
        return next;
    });

    // Obstacle markers — always visible, flagged red if penetrating in the active procedure
    useEffect(() => {
        if (!mapInst.current || projectLoading) return;
        const map = mapInst.current;
        obsLyrs.current.forEach(l => map.removeLayer(l));
        obsLyrs.current = [];

        const penetratingIds = new Set((activeProc?.results || []).filter(r => !r.clears).map(r => r.obstacle_id));
        const bounds = [];
        getObstaclesToRender(obstacles || [], project?.id, showObstacles).forEach(o => {
            if (o.latitude == null || o.longitude == null) return;
            const ll = [o.latitude, o.longitude];
            const status = activeProc ? (penetratingIds.has(o.id) ? 'penetrated' : 'clear') : undefined;
            const icon = getObstacleIcon(L, o, status, showObsLabels);
            const m = L.marker(ll, { icon })
                .bindPopup(`<b>${o.name || 'Unnamed Obstacle'}</b><br/>ID: ${o.id}<br/>ELEVATION: ${o.elevation_m ?? '—'} m`);
            if (onObstacleContextMenu) {
                m.on('contextmenu', e => {
                    e.originalEvent.preventDefault();
                    onObstacleContextMenu(o, e.originalEvent.clientX, e.originalEvent.clientY);
                });
            }
            m.addTo(map);
            obsLyrs.current.push(m);
            if (penetratingIds.has(o.id)) bounds.push(ll);
        });
        addHullPolygonsToMap(map, obstacles, project?.id, obsLyrs);
    }, [obstacles, activeProc, projectLoading, showObstacles, showObsLabels, onObstacleContextMenu]);

    // STAR geometry — tracks / primary+secondary areas / waypoints (flyby/flyover symbols)
    useEffect(() => {
        if (!mapInst.current) return;
        const map = mapInst.current;
        starLyrs.current.forEach(l => map.removeLayer(l));
        starLyrs.current = [];

        const bounds = [];
        procedures.forEach(proc => {
            if (!visibleIds.has(proc.id)) return;
            const isActive = proc.id === activeId;

            (proc.features || []).forEach(f => {
                const role = starResolveRole(f);
                const baseOpacity = isActive ? 1 : 0.35;
                const cleanName = (f.name || '').replace(/^WP\s+/i, '');

                if (f.type === 'point') {
                    const ll = [f.coords[0], f.coords[1]];
                    bounds.push(ll);
                    const m = L.marker(ll, { icon: waypointIcon(f.turn_type), opacity: baseOpacity });
                    m.addTo(map);
                    starLyrs.current.push(m);
                    if (isActive) {
                        starLyrs.current.push(waypointLabel(map, ll, cleanName || 'Waypoint'));
                    }
                } else {
                    const meta = starRoleMeta[role];
                    if (!meta) return;
                    const latlngs = f.coords.map(c => [c[0], c[1]]);
                    latlngs.forEach(ll => bounds.push(ll));
                    if (f.type === 'line') {
                        const line = L.polyline(latlngs, {
                            color: meta.color, weight: meta.weight, opacity: meta.opacity * baseOpacity,
                        }).bindTooltip(`${cleanName || meta.label}`, { sticky: true });
                        line.addTo(map);
                        starLyrs.current.push(line);
                        if (isActive) {
                            const arrow = starAddArrowhead(map, latlngs, meta.color, meta.opacity);
                            if (arrow) starLyrs.current.push(arrow);
                        }
                    } else if (f.type === 'polygon') {
                        const poly = L.polygon(latlngs, {
                            color: meta.color, weight: meta.weight, opacity: meta.opacity * baseOpacity,
                            fillColor: meta.fillColor, fillOpacity: meta.fillOpacity * baseOpacity,
                        }).bindTooltip(`${proc.procedure_name}<br/>${cleanName || meta.label}`, { sticky: true });
                        poly.addTo(map);
                        starLyrs.current.push(poly);
                    }
                }
            });
        });

        if (bounds.length > 0 && activeProc) {
            try {
                const b = L.latLngBounds(bounds);
                if (b.isValid()) map.fitBounds(b, { padding: [40, 40], maxZoom: 11 });
            } catch (e) {}
        }
    }, [procedures, visibleIds, activeId, activeProc, starRoleMeta]);

    const captureMapDataUrl = async () => {
        const container = mapRef.current;
        if (!container) return null;
        try {
            const containerRect = container.getBoundingClientRect();
            const w = Math.round(containerRect.width);
            const h = Math.round(containerRect.height);

            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#07111e';
            ctx.fillRect(0, 0, w, h);

            const tileEls = [...container.querySelectorAll('.leaflet-tile:not(.leaflet-tile-loading)')];
            await Promise.all(tileEls.map(async tile => {
                if (!tile.src) return;
                const tRect = tile.getBoundingClientRect();
                const tx = Math.floor(tRect.left - containerRect.left);
                const ty = Math.floor(tRect.top - containerRect.top);
                const tw = Math.ceil(tRect.width) + 1;
                const th = Math.ceil(tRect.height) + 1;
                try {
                    const resp = await fetch(tile.src, { mode: 'cors' });
                    if (!resp.ok) return;
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    await new Promise(resolve => {
                        const img = new Image();
                        img.onload = () => { ctx.drawImage(img, tx, ty, tw, th); URL.revokeObjectURL(url); resolve(); };
                        img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                        img.src = url;
                    });
                } catch {}
            }));

            const svgEl = container.querySelector('.leaflet-overlay-pane svg');
            if (svgEl) {
                const svgRect = svgEl.getBoundingClientRect();
                const sx = Math.round(svgRect.left - containerRect.left);
                const sy = Math.round(svgRect.top - containerRect.top);
                const clone = svgEl.cloneNode(true);
                clone.style.transform = ''; clone.style.left = '0'; clone.style.top = '0';
                const svgStr = new XMLSerializer().serializeToString(clone);
                const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                await new Promise(resolve => {
                    const img = new Image();
                    img.onload = () => { ctx.drawImage(img, sx, sy, Math.round(svgRect.width), Math.round(svgRect.height)); URL.revokeObjectURL(url); resolve(); };
                    img.onerror = () => { URL.revokeObjectURL(url); resolve(); };
                    img.src = url;
                });
            }

            const markerEls = [...container.querySelectorAll('.leaflet-marker-icon')];
            for (const marker of markerEls) {
                const mRect = marker.getBoundingClientRect();
                const x = Math.round(mRect.left - containerRect.left);
                const y = Math.round(mRect.top - containerRect.top);
                const img = marker.querySelector('img');
                if (img && img.complete && img.naturalWidth) {
                    try { ctx.drawImage(img, x, y, Math.round(mRect.width), Math.round(mRect.height)); } catch {}
                }
            }

            return canvas.toDataURL('image/jpeg', 0.88);
        } catch (err) {
            console.error('Failed to capture screenshot:', err);
            return null;
        }
    };

    const handleScreenshot = async (procId) => {
        const dataUrl = await captureMapDataUrl();
        if (!dataUrl) return;
        try {
            await saveStarScreenshot(procId, project?.id, dataUrl);
            setCapturedPics(prev => ({ ...prev, [procId]: true }));
        } catch (err) { console.error('Failed to save STAR screenshot:', err); }
    };

    const handleClearScreenshot = async (procId) => {
        try {
            await deleteStarScreenshot(procId, project?.id);
            setCapturedPics(prev => {
                const next = { ...prev };
                delete next[procId];
                return next;
            });
        } catch (err) { console.error(err); }
    };

    const obsRows = activeProc?.results || [];
    const segRows = activeProc?.segments_summary || [];

    const renderObstacleResults = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '14px 20px', height: '100%', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.white }}>Obstacle Clearance — {activeProc?.procedure_name || ''}</div>
                {obsRows.length > 0 && (
                    <button
                        onClick={() => {
                            const headers = ["Obstacle", "Segment", "Area", "h_obs (m)", "MOC (m)", "Floor (m)", "Margin (m)", "Verdict", "Controlling"];
                            const rows = obsRows.map(r => [
                                r.obstacle_name, r.segment, r.area_type, r.h_obs_m, r.moc_m, r.floor_m, r.margin_m, r.verdict, r.is_controlling ? 'Yes' : ''
                            ]);
                            exportToCsv(headers, rows, `${activeProc.procedure_name.replace(/\s+/g, '_')}_STAR_Assessment.csv`);
                        }}
                        style={{ padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', background: T.blue, color: T.white, fontSize: 11, fontWeight: 700 }}
                    >
                        📥 Export CSV
                    </button>
                )}
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, textAlign: 'left' }}>
                    <thead style={{ position: 'sticky', top: 0, background: T.bg0, zIndex: 1 }}>
                        <tr>
                            <th style={{ padding: '6px 12px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Obstacle</th>
                            <th style={{ padding: '6px 12px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Segment</th>
                            <th style={{ padding: '6px 12px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Area</th>
                            <th style={{ padding: '6px 12px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>h_obs (m)</th>
                            <th style={{ padding: '6px 12px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>MOC (m)</th>
                            <th style={{ padding: '6px 12px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Floor (m)</th>
                            <th style={{ padding: '6px 12px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Margin (m)</th>
                            <th style={{ padding: '6px 12px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Verdict</th>
                        </tr>
                    </thead>
                    <tbody>
                        {obsRows.length > 0 ? obsRows.map((r, i) => (
                            <tr key={i} style={{ borderBottom: `1px solid ${T.border}33`, background: !r.clears ? 'rgba(232,64,64,0.08)' : 'transparent' }}>
                                <td style={{ padding: '6px 12px', color: T.white }}>{r.obstacle_name}<div style={{ fontSize: 10, color: T.muted }}>Elev {r.obstacle_elev_m} m · VTol {r.v_tolerance_m} m</div></td>
                                <td style={{ padding: '6px 12px', color: T.white }}>{r.segment}<div style={{ fontSize: 10, color: T.muted }}>Seg {r.segment_index}</div></td>
                                <td style={{ padding: '6px 12px', color: r.area_type === 'secondary' ? '#ffaa00' : '#0078ff', textTransform: 'capitalize' }}>{r.area_type}</td>
                                <td style={{ padding: '6px 12px', color: T.white }}>{r.h_obs_m}</td>
                                <td style={{ padding: '6px 12px', color: T.white }}>{r.moc_m}</td>
                                <td style={{ padding: '6px 12px', color: T.white }}>{r.floor_m}</td>
                                <td style={{ padding: '6px 12px', fontWeight: 700, color: r.margin_m >= 0 ? T.safe : T.danger }}>{r.margin_m}</td>
                                <td style={{ padding: '6px 12px' }}>
                                    {r.clears
                                        ? <span style={{ color: T.safe }}>Clear</span>
                                        : <span style={{ color: T.danger, fontWeight: 700 }}>Penetrates</span>}
                                    {r.is_controlling && <span style={{ color: T.blue, fontSize: 9, marginLeft: 6 }}>[Controlling]</span>}
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="8" style={{ padding: '20px', textAlign: 'center', color: T.muted }}>
                                    {loading ? 'Loading STAR arrival procedures…' : error ? error :
                                        (activeProc ? 'No obstacles fall within this procedure\'s protection areas.' : 'No STAR arrival procedures within 30 NM of the obstacle hull.')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderSegmentSummary = () => (
        <div style={{ padding: '0 20px 14px', flexShrink: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Lowest Safe Minimum per Segment
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, textAlign: 'left' }}>
                <thead>
                    <tr>
                        <th style={{ padding: '4px 10px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Segment</th>
                        <th style={{ padding: '4px 10px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Published (ft)</th>
                        <th style={{ padding: '4px 10px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Required (ft)</th>
                        <th style={{ padding: '4px 10px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Rounded (ft)</th>
                        <th style={{ padding: '4px 10px', color: T.muted, borderBottom: `1px solid ${T.border}` }}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {segRows.map(s => (
                        <tr key={s.segment_index} style={{ borderBottom: `1px solid ${T.border}22` }}>
                            <td style={{ padding: '4px 10px', color: T.white }}>{s.segment_name}<span style={{ color: T.muted, fontSize: 9 }}> (Seg {s.segment_index})</span></td>
                            <td style={{ padding: '4px 10px', color: T.white }}>{s.published_ft}</td>
                            <td style={{ padding: '4px 10px', color: T.white }}>{s.required_ft}</td>
                            <td style={{ padding: '4px 10px', color: T.white, fontWeight: 700 }}>{s.rounded_ft}</td>
                            <td style={{ padding: '4px 10px' }}>
                                {s.safe
                                    ? <span style={{ color: T.safe }}>Safe</span>
                                    : <span style={{ color: T.danger, fontWeight: 700 }}>Raise minimum</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderResultsContent = () => (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ flex: 1, overflow: 'hidden' }}>{renderObstacleResults()}</div>
            {segRows.length > 0 && renderSegmentSummary()}
        </div>
    );

    async function handleExportAixm() {
        if (!project?.id) return;
        setExportingAixm(true);
        try {
            const resp = await fetch(`${API_BASE}/projects/${project.id}/export/aixm?surfaces=star`);
            if (!resp.ok) {
                const body = await resp.json().catch(() => ({}));
                throw new Error(body.detail || `Export failed (${resp.status})`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const cd = resp.headers.get('content-disposition') || '';
            const nameMatch = cd.match(/filename="?([^";]+)"?/);
            link.download = nameMatch ? nameMatch[1] : `project_${project.id}_export_STAR_AIXM51.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export AIXM:', err);
            alert(err.message || 'Failed to export AIXM. Please check your network connection and try again.');
        } finally {
            setExportingAixm(false);
        }
    }

    return (
        <div style={{ display: 'flex', flex: 1, height: '100%', minHeight: 0, background: T.bg2, position: 'relative' }}>

            {/* Sidebar */}
            <div style={{ width: 320, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', background: T.bg1, zIndex: 10, flexShrink: 0 }}>
                <div style={{ padding: 16, borderBottom: `1px solid ${T.border}33` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.white, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            STAR Arrival OIS
                        </div>
                        {procedures.length > 0 && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: allVisible ? T.white : T.muted, cursor: 'pointer', userSelect: 'none' }}>
                                <input
                                    type="checkbox"
                                    checked={allVisible}
                                    ref={el => { if (el) el.indeterminate = someVisible; }}
                                    onChange={e => toggleAll(e.target.checked)}
                                    style={{ accentColor: T.blue, cursor: 'pointer' }}
                                />
                                All
                            </label>
                        )}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>
                        {loading ? 'Loading…' : `${procedures.length} procedure${procedures.length === 1 ? '' : 's'} within 30 NM of obstacle hull`}
                    </div>
                    <button
                        onClick={handleExportAixm}
                        disabled={exportingAixm}
                        style={{ marginTop: 10, padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.blue}44`, cursor: exportingAixm ? 'default' : 'pointer', background: `${T.blue}11`, color: T.blue, fontSize: 10, fontWeight: 700, opacity: exportingAixm ? 0.6 : 1 }}
                        title="Export obstacles, aerodrome data, waypoints and STAR arrival procedures (StandardInstrumentArrival) as AIXM 5.1"
                    >
                        {exportingAixm ? '⏳…' : '📤 AIXM'}
                    </button>
                </div>

                {error ? (
                    <div style={{ padding: 16, color: T.danger, fontSize: 11 }}>Error: {error}</div>
                ) : !loading && procedures.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: T.muted, fontSize: 12 }}>
                        No STAR arrival procedures within 30 NM of this project's obstacles.
                    </div>
                ) : (
                    <div style={{ flex: 1, overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {procedures.map(proc => {
                            const penCount = (proc.results || []).filter(r => !r.clears).length;
                            const isActive = proc.id === activeId;
                            const isVisible = visibleIds.has(proc.id);
                            return (
                                <div
                                    key={proc.id}
                                    onClick={() => setActiveId(proc.id)}
                                    style={{
                                        padding: 12, borderRadius: 8, cursor: 'pointer',
                                        border: isActive ? '1px solid var(--blue-glass-border)' : '1px solid transparent',
                                        background: isActive ? 'var(--blue-glass-fill)' : 'transparent',
                                        opacity: isVisible ? 1 : 0.45,
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <input
                                            type="checkbox"
                                            checked={isVisible}
                                            onClick={e => e.stopPropagation()}
                                            onChange={e => toggleOne(proc.id, e.target.checked)}
                                            style={{ accentColor: T.blue, cursor: 'pointer', flexShrink: 0 }}
                                        />
                                        <span style={{ fontWeight: 600, color: isActive ? T.blueLt : T.white, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {proc.procedure_name}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 11, color: T.muted, marginTop: 6 }}>
                                        RWY {proc.runway} · Minima {proc.minima_ft?.join('/')} ft
                                        {penCount > 0 && <span style={{ color: T.danger, fontWeight: 700, marginLeft: 6 }}>⚠ {penCount} penetration{penCount === 1 ? '' : 's'}</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div style={{ padding: 12, borderTop: `1px solid ${T.border}33`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 11, color: T.muted, textAlign: 'center' }}>
                        {result.obstacle_count} obstacle{result.obstacle_count !== 1 ? 's' : ''} · {procedures.length} procedure{procedures.length !== 1 ? 's' : ''} found
                    </div>
                    {activeProc && (
                        <div style={{ display: 'flex', gap: 6 }}>
                            <button
                                onClick={() => handleScreenshot(activeProc.id)}
                                style={{
                                    flex: 1, padding: '8px 12px', border: 'none', borderRadius: 6,
                                    background: capturedPics[activeProc.id] ? 'rgba(46,201,139,0.15)' : T.blue,
                                    color: capturedPics[activeProc.id] ? T.safe : T.white, fontSize: 11,
                                    fontWeight: 700, cursor: 'pointer',
                                }}
                            >
                                {capturedPics[activeProc.id] ? '✓ Captured' : '📷 Capture Screenshot'}
                            </button>
                            {capturedPics[activeProc.id] && (
                                <button
                                    onClick={() => handleClearScreenshot(activeProc.id)}
                                    title="Clear Capture"
                                    style={{ padding: '8px', background: 'rgba(255,255,255,0.07)', border: `1px solid ${T.border}`, borderRadius: 6, color: T.danger, cursor: 'pointer' }}
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Map & Results Panel */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                    <ShieldControl top={92} shieldOn={shieldOn} toggleShield={toggleShield} shieldData={shieldData} shieldLoading={shieldLoading} shieldError={shieldError} projectMaxTotalElev={projectMaxTotalElev} projectPenetratingMaxTotalElev={projectPenetratingMaxTotalElev} radiusKm={radiusKm} setRadiusKm={setRadiusKm} exportCsv={exportCsv} />
                    <MapMeasureTool map={mapState} top={52} tableOffset={procedures.length ? (resultsCollapsed ? 40 : 340) : 0} />
                    <MapCoordinateViewer map={mapState} />
                    <div ref={mapRef} style={{ width: '100%', height: '100%', zIndex: 1 }} />

                    <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 100, display: 'flex', gap: 4, background: T.bg1, padding: 4, borderRadius: 8, border: `1px solid ${T.border}` }}>
                        {Object.entries(TILE_LAYERS).map(([k, l]) => (
                            <button
                                key={k}
                                onClick={() => setMapMode(k)}
                                style={{
                                    padding: '4px 10px', borderRadius: 6, border: 'none', fontSize: 11, cursor: 'pointer',
                                    background: mapMode === k ? 'rgba(58, 138, 196, 0.15)' : 'transparent',
                                    color: mapMode === k ? T.blueLt : T.muted, fontWeight: mapMode === k ? 700 : 400
                                }}
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>
                </div>

                {procedures.length > 0 && (
                    <div style={{
                        background: T.bg1, borderTop: `1px solid ${T.border}`,
                        display: 'flex', flexDirection: 'column',
                        height: resultsCollapsed ? 40 : resultsHeight,
                        transition: 'height 0.25s ease-in-out',
                        overflow: 'hidden'
                    }}>
                        {!resultsCollapsed && <ResizeHandle onMouseDown={startResultsResize} />}
                        <div style={{
                            padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            cursor: 'pointer', borderBottom: `1px solid ${T.border}22`, background: T.bg2
                        }} onClick={() => setResultsCollapsed(!resultsCollapsed)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: T.blueLt }}>STAR Arrival Report</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {!resultsPoppedOut && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setResultsPoppedOut(true); setResultsCollapsed(true); }}
                                        style={{ background: 'none', border: 'none', color: T.blueLt, cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}
                                        title="Pop out results to a movable window"
                                    >
                                        🗖 Pop Out
                                    </button>
                                )}
                                <span style={{ color: T.muted, fontSize: 12 }}>{resultsCollapsed ? '▲' : '▼'}</span>
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {resultsPoppedOut ? (
                                <div style={{ padding: 20, textAlign: 'center', color: T.muted, fontSize: 13 }}>
                                    Results are popped out.
                                    <button
                                        onClick={() => { setResultsPoppedOut(false); setResultsCollapsed(false); }}
                                        style={{ background: 'none', border: 'none', color: T.blue, textDecoration: 'underline', cursor: 'pointer', marginLeft: 6, fontWeight: 600 }}
                                    >
                                        Bring back
                                    </button>
                                </div>
                            ) : renderResultsContent()}
                        </div>
                    </div>
                )}
            </div>

            {resultsPoppedOut && procedures.length > 0 && (
                <DraggableModal
                    title={`${project?.name || ''} - STAR Arrival Results`}
                    onClose={() => setResultsPoppedOut(false)}
                    width={900}
                    height={560}
                >
                    {renderResultsContent()}
                </DraggableModal>
            )}
        </div>
    );
}
